import { requestAnimationFrameHidden } from '@/lib/utils'
import type {
  JoinAction,
  JoinMessage,
  ReadyMessage,
  SitDownAction,
  WebsocketMessage
} from '@acepoker/contract/acepoker'

export function createBridge({
  onRoomStateUpdate,
  onClose,
  onError,
  onJoin,
  onReady,
  onTableClosed,

  token,
  name,
  roomId
}: {
  token: string | null
  name: string | null
  roomId: string | null
  onRoomStateUpdate: (updates: WebsocketMessage[]) => void
  onError: (message: string) => void
  onJoin: (message: JoinMessage) => void
  onReady: (message: ReadyMessage) => void
  onClose: () => void
  onTableClosed: () => void
}) {
  let updates: WebsocketMessage[] = []
  let updateStarted = false

  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
  const socket = new WebSocket(
    `${protocol}://${import.meta.env.VITE_POKER_SERVER_DOMAIN || window.location.host}/ws/${roomId}?token=${token}`
  )

  socket.onopen = () => {
    console.debug('Connected to AcePoker server')
  }

  socket.onmessage = (event) => {
    const payload: WebsocketMessage = JSON.parse(event.data)
    switch (payload.type) {
      case 'pong':
        break
      case 'ready':
        onReady(payload)
        break
      case 'full':
      case 'delta':
        updates.push(payload)
        if (updateStarted) return
        updateStarted = true
        requestAnimationFrameHidden(() => {
          onRoomStateUpdate(updates)
          updateStarted = false
          updates = []
        })

        break
      case 'error':
        onError(payload.message)
        break
      case 'join':
        onJoin(payload)
        break
      case 'table-closed':
        onTableClosed()
        break
    }
  }

  const fold = () => {
    socket.send(JSON.stringify({ action: 'fold' }))
  }

  const call = () => {
    socket.send(JSON.stringify({ action: 'call' }))
  }

  const check = () => {
    socket.send(JSON.stringify({ action: 'check' }))
  }

  const raise = (amount: number) => {
    socket.send(JSON.stringify({ action: 'raise', amount }))
  }

  const bet = (amount: number) => {
    socket.send(JSON.stringify({ action: 'bet', amount }))
  }

  const join = (payload: Omit<JoinAction, 'action'>) => {
    const message: JoinAction = {
      action: 'join',
      ...payload
    }
    socket.send(JSON.stringify(message))
  }

  const sitDown = (payload: Omit<SitDownAction, 'action'>) => {
    const message: SitDownAction = {
      action: 'sit-down',
      ...payload
    }
    socket.send(JSON.stringify(message))
  }

  const ping = () => {
    socket.send(JSON.stringify({ action: 'ping' }))
  }

  const close = () => {
    socket.close()
  }

  const leaveTable = () => {
    socket.send(JSON.stringify({ action: 'leave-table' }))
  }

  const closeTable = () => {
    socket.send(JSON.stringify({ action: 'close-table' }))
  }

  const sitOut = () => {
    socket.send(JSON.stringify({ action: 'sit-out' }))
  }

  const sitIn = () => {
    socket.send(JSON.stringify({ action: 'sit-in' }))
  }

  const standUp = () => {
    socket.send(JSON.stringify({ action: 'stand-up' }))
  }

  socket.onclose = () => {
    console.debug('Disconnected from AcePoker server')
    onClose()
  }

  return {
    fold,
    call,
    check,
    raise,
    bet,
    join,
    sitDown,
    ping,
    close,
    leaveTable,
    closeTable,
    sitOut,
    sitIn,
    standUp
  }
}
